import React, { useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import ApiService from '../../_services/ApiService';
import StudyResourceService from '../../_services/StudyResourceService';
import { SkeletonLoader } from '../../_components/skeleton-loader/SkeletonLoader';

const RESOURCE_TYPE_ICONS: Record<number, string> = {
  1: "📥",   // Download
  5: "🔗",   // Reference
  6: "📝",   // Article
  13: "📚",  // E-Book
  16: "🎥",  // Streaming Video
  31: "🎬",  // Vimeo Video
  32: "▶️",   // YouTube Video
  36: "📋",  // Online Test
};

const TYPE_FILTER_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "1", label: "Download" },
  { value: "6", label: "Article" },
  { value: "5", label: "Reference" },
  { value: "13", label: "E-Book" },
  { value: "16", label: "Video" },
  { value: "31", label: "Vimeo Video" },
  { value: "32", label: "YouTube Video" },
  { value: "36", label: "Online Test" },
];

function getResourceIcon(typeId: number): string {
  return RESOURCE_TYPE_ICONS[typeId] || "📄";
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function isS3Url(url: string): boolean {
  return url.includes(".s3.") && url.includes("amazonaws.com");
}

function formatFileSize(bytes: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const PAGE_SIZE = 20;

const MyResourcesPage: React.FC = () => {
  const history = useHistory();
  const [resources, setResources] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [typeId, setTypeId] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [expandedArticle, setExpandedArticle] = useState<number | null>(null);
  const [signingId, setSigningId] = useState<number | null>(null);

  const fetchResources = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (typeId) params.typeId = Number(typeId);
      if (search) params.search = search;

      const resp = await ApiService.getMyStaffResources(params);
      const data = resp.data;
      if (data.success) {
        setResources(data.data || []);
        setTotalCount(data.totalCount || 0);
      } else {
        setResources([]);
        setTotalCount(0);
      }
    } catch {
      setResources([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, typeId, search]);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setPage(0);
    setSearch(searchInput);
  }

  function handleTypeChange(e: React.ChangeEvent<HTMLSelectElement>) {
    setPage(0);
    setTypeId(e.target.value);
  }

  async function openResource(e: React.MouseEvent, resource: any) {
    e.stopPropagation();
    const url = resource.url;
    if (!url) return;

    if (!isS3Url(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    // S3 URL — get signed URL
    const newWindow = window.open("", "_blank");
    setSigningId(resource.id);
    try {
      const signedUrl = await StudyResourceService.getSignedUrl(url);
      if (newWindow) {
        newWindow.location.href = signedUrl;
      }
    } catch (err) {
      console.error("Failed to get signed URL:", err);
      if (newWindow) {
        newWindow.location.href = url;
      }
    } finally {
      setSigningId(null);
    }
  }

  function toggleArticle(resourceId: number) {
    setExpandedArticle(prev => prev === resourceId ? null : resourceId);
  }

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
      <h4 style={{ marginBottom: '20px', fontWeight: 600, color: 'var(--text-strong)' }}>My Resources</h4>

      {/* Filters Row */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          value={typeId}
          onChange={handleTypeChange}
          style={{
            padding: '8px 12px',
            borderRadius: '6px',
            border: '1px solid var(--border-strong)',
            fontSize: '14px',
            background: 'var(--surface-card)',
            minWidth: '160px',
          }}
        >
          {TYPE_FILTER_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '8px', flex: 1, minWidth: '200px' }}>
          <input
            type="text"
            placeholder="Search by name..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid var(--border-strong)',
              fontSize: '14px',
              flex: 1,
            }}
          />
          <button
            type="submit"
            className="btn btn-primary btn-sm"
          >
            Search
          </button>
          {search && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => { setSearchInput(''); setSearch(''); setPage(0); }}
            >
              Clear
            </button>
          )}
        </form>
      </div>

      {/* Results */}
      {loading ? (
        <div style={{ padding: '20px' }}>
          <SkeletonLoader type="table" count={5} />
        </div>
      ) : resources.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: 'var(--text-muted)',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📂</div>
          <div style={{ fontSize: '18px', fontWeight: 500, marginBottom: '8px' }}>
            No resources have been issued to you
          </div>
          <div style={{ fontSize: '14px' }}>
            {search || typeId ? 'Try adjusting your filters.' : 'Check back later for new resources.'}
          </div>
        </div>
      ) : (
        <>
          <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '12px' }}>
            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount} resources
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {resources.map((resource: any) => {
              const isArticle = resource.typeId === 6;
              const hasContent = !!resource.content;
              const hasAttachment = !!resource.url;
              const isExpanded = expandedArticle === resource.id;

              return (
                <div key={resource.id}>
                  {/* Resource Row */}
                  <div
                    onClick={() => {
                      if (isArticle) {
                        history.push(`/my-resources/${resource.id}`);
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '14px 16px',
                      background: 'var(--surface-card)',
                      borderRadius: '8px',
                      cursor: isArticle || hasAttachment ? 'pointer' : 'default',
                      border: '1px solid var(--border-default)',
                      transition: 'box-shadow 0.15s, border-color 0.15s',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.borderColor = 'var(--border-strong)';
                      e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.borderColor = 'var(--border-default)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, minWidth: '36px' }}>
                      <span style={{ fontSize: '24px' }}>
                        {getResourceIcon(resource.typeId)}
                      </span>
                      <span style={{ fontSize: '10px', color: 'var(--text-faint)', fontFamily: 'monospace', marginTop: '2px' }}>
                        #{resource.id}
                      </span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '15px',
                        fontWeight: 500,
                        color: 'var(--text-strong)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {resource.name}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-faint)', marginTop: '2px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <span>{formatDate(resource.startDate)} — {formatDate(resource.endDate)}</span>
                        {resource.fileSizeInByte ? (
                          <span style={{ color: 'var(--text-muted)' }}>{formatFileSize(resource.fileSizeInByte)}</span>
                        ) : null}
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                      {/* Open article in viewer page */}
                      {isArticle && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={(e) => { e.stopPropagation(); history.push(`/my-resources/${resource.id}`); }}
                          style={{ fontSize: '12px', padding: '4px 10px' }}
                        >
                          Open
                        </button>
                      )}

                      {/* Download/Open button for non-article resources with URLs */}
                      {!isArticle && hasAttachment && (
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={signingId === resource.id}
                          onClick={(e) => openResource(e, resource)}
                          style={{ fontSize: '12px', padding: '4px 10px' }}
                        >
                          {signingId === resource.id ? 'Opening...' : (resource.typeId === 1 ? 'Download' : 'Open')}
                        </button>
                      )}

                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
              marginTop: '20px',
            }}>
              <button
                className="btn btn-secondary btn-sm"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
              >
                Previous
              </button>
              <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                Page {page + 1} of {totalPages}
              </span>
              <button
                className="btn btn-secondary btn-sm"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MyResourcesPage;
